import React from "react";
import { ClerkProvider, useSession, SignIn } from "@clerk/clerk-react";
import { i18n } from "../lib/i18n";

// EmbeddedClerkAuth — a stripped-down variant of <ClerkAuth /> intended to be
// rendered INSIDE an existing card on the page (e.g. the HSDS claim landing).
// It hides Clerk's own card chrome, header, and footer so the SignIn form
// blends into its wrapper. The companion CSS overrides in application.css
// target `[data-controller~="embedded-clerk-auth"] .cl-*` to defeat Clerk's
// internal width caps that Tailwind utilities alone can't beat on specificity.

const PRIMARY = "#287379";
const PRIMARY_HOVER = "#1F585C";

const SignInForm: React.FC<{ redirectUrl?: string }> = ({ redirectUrl }) => {
  const { isLoaded } = useSession();

  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center p-8 w-full">
        <span
          style={{ color: PRIMARY }}
          className="loading loading-spinner loading-lg animate-spin"
        ></span>
      </div>
    );
  }

  return (
    <SignIn
      appearance={{
        elements: {
          formButtonPrimary: `bg-${PRIMARY} hover:bg-${PRIMARY_HOVER}`,
          footerActionLink: `text-${PRIMARY} hover:text-${PRIMARY_HOVER}`,
        },
      }}
      fallbackRedirectUrl={redirectUrl}
      forceRedirectUrl={redirectUrl}
      signUpForceRedirectUrl={redirectUrl}
      signInUrl={`/sign-in${redirectUrl ? `?redirect_url=${redirectUrl}` : ""}`}
      signUpUrl={`/sign-up${redirectUrl ? `?redirect_url=${redirectUrl}` : ""}`}
      withSignUp={true}
    />
  );
};

export const EmbeddedClerkAuth: React.FC<{
  publishableKey: string;
  redirectUrl?: string;
}> = ({ publishableKey, redirectUrl }) => {
  if (!publishableKey) {
    console.error("Missing publishableKey");
    return (
      <div className="text-red-500">
        {i18n.t("clerk_auth.missing_publishable_key")}
      </div>
    );
  }

  return (
    <ClerkProvider
      publishableKey={publishableKey}
      appearance={{
        baseTheme: undefined,
        variables: {
          colorPrimary: PRIMARY,
          colorTextOnPrimaryBackground: "white",
        },
      }}
    >
      <SignInForm redirectUrl={redirectUrl} />
    </ClerkProvider>
  );
};
